import React, { Fragment, useState, useCallback } from 'react';
import { DateUtils } from 'react-day-picker';
import moment from 'moment';
import styled from 'styled-components';
import { Modal, Button, Spacer, Flexer, SegmentedButton, ButtonRound } from '@ontruck/orc';
import { injectIntl } from 'react-intl';
import Calendar from 'components/Calendar';

const FlexerChildStyled = styled(Flexer.Child)`
  color: var(--primary-top-bar-color);
`;
const initialRange = { from: undefined, to: undefined };

const DateFilter = ({ onChange, value, intl, ...props }) => {
  const [opened, setOpened] = useState(false);
  const [range, setRange] = useState(value ? value : initialRange);
  const t = intl.messages.modules.Filters.date_range;
  const handleDayClick = useCallback(
    (day) => {
      const newRange = DateUtils.addDayToRange(day, range);
      setRange(newRange);
    },
    [range, setRange]
  );

  const handleRangeClick = useCallback(() => {
    setOpened(true);
  }, [setOpened]);

  const handleModalClose = useCallback(() => {
    setOpened(false);
    setRange(initialRange);
  }, [setOpened]);

  const handleSubmit = useCallback(() => {
    setOpened(false);
    onChange(range);
  }, [range, onChange, setOpened]);

  const handleSelectorChange = useCallback(
    (_, { value }) => {
      if (value === 'range') {
        return;
      }

      onChange(null);
      setRange(initialRange);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setRange(initialRange);
  }, [setRange, onChange]);

  const { from, to } = range;
  const modifiers = { start: from, end: to };
  const selectorValue = value ? 'range' : 'last_two_months';
  const hasValue = !!value;

  const disabledDays = from
    ? {
        before: !to
          ? moment(from).subtract(2, 'months').toDate()
          : moment(to).subtract(2, 'months').toDate(),
        after: moment(from).add(2, 'months').toDate(),
      }
    : undefined;

  return (
    <Fragment>
      <Flexer.Parent {...props}>
        {hasValue && (
          <Flexer.Parent>
            <FlexerChildStyled mRight="XS">
              {moment(from).format('Do MMM YYYY')} - {moment(to).format('Do MMM YYYY')}
            </FlexerChildStyled>
            <div>
              <ButtonRound skin="primary" size="XXS" onClick={handleClear} icon="close" />
            </div>
          </Flexer.Parent>
        )}
        {!hasValue && (
          <SegmentedButton.Parent size="M" value={selectorValue} onChange={handleSelectorChange}>
            <SegmentedButton.Child value="last_two_months" label={t.last_months} />
            <SegmentedButton.Child
              onChange={handleRangeClick}
              value="range"
              label={`${t.select_range}`}
            />
          </SegmentedButton.Parent>
        )}
      </Flexer.Parent>
      <Modal show={opened} withPadding={false} headerComponent={t.title} onClose={handleModalClose}>
        <Spacer p="S">
          <Flexer.Parent alignItems="center" justifyContent="center">
            <Calendar
              numberOfMonths={2}
              fixedWeeks
              selectedDays={[from, { from, to }]}
              onDayClick={handleDayClick}
              modifiers={modifiers}
              disabledDays={disabledDays}
            />
          </Flexer.Parent>
          <Flexer.Parent mTop="M" justifyContent="flex-end">
            <Button text={t.cancel} skin="secondary" nude onClick={handleModalClose} />
            <Button text={t.apply} disabled={!from || !to} onClick={handleSubmit} />
          </Flexer.Parent>
        </Spacer>
      </Modal>
    </Fragment>
  );
};

DateFilter.filterName = 'RangeDateFilter';

export default injectIntl(DateFilter);
